import React, { useState, useEffect } from 'react';
// // // import { useTranslation } from 'react-i18next';   // Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard, Plus, Edit, Trash2, Search, Filter, RefreshCw, Eye, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import {
  collection, query, orderBy, limit, getDocs, doc, updateDoc, deleteDoc, addDoc, where, startAfter, getCountFromServer
} from 'firebase/firestore';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';

const RemittanceAccountManager = () => {
  // const { t } = useTranslation();
   // Admin은 다국어 지원 불필요const { showToastMessage } = useToastMessage();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [modalType, setModalType] = useState('create');
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    accountNumber: '',
    bankName: '',
    accountHolder: '',
    status: 'active',
    description: ''
  });
  
  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0
  });

  const statusOptions = [
    { value: 'all', label: '전체' },
    { value: 'active', label: '활성' },
    { value: 'inactive', label: '비활성' },
    { value: 'pending', label: '대기중' },
  ];

  // Load data on component mount
  useEffect(() => {
    fetchAccounts();
    loadStatistics();
  }, [currentPage, filterStatus]);

  // Fetch accounts from Firebase
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const collectionPath = `artifacts/${appId}/remittanceAccounts`;
      
      let q = query(
        collection(db, collectionPath),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      if (filterStatus !== 'all') {
        q = query(
          collection(db, collectionPath),
          where('status', '==', filterStatus),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
      }

      if (currentPage > 1 && lastVisible) {
        q = query(
          collection(db, collectionPath),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(20)
        );
      }

      const snap = await getDocs(q);
      const accountsData = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
      }));

      setAccounts(accountsData);
      
      if (snap.docs.length > 0) {
        setLastVisible(snap.docs[snap.docs.length - 1]);
      }

      // Calculate total pages
      const countSnap = await getCountFromServer(collection(db, collectionPath));
      setTotalPages(Math.ceil(countSnap.data().count / 20));
    } catch (error) {
      console.error('Accounts loading failed:', error);
      showToastMessage('계좌 정보 로딩에 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      const collectionPath = `artifacts/${appId}/remittanceAccounts`;
      const allQuery = query(collection(db, collectionPath));
      const activeQuery = query(collection(db, collectionPath), where('status', '==', 'active'));
      const inactiveQuery = query(collection(db, collectionPath), where('status', '==', 'inactive'));
      const pendingQuery = query(collection(db, collectionPath), where('status', '==', 'pending'));

      const [allSnap, activeSnap, inactiveSnap, pendingSnap] = await Promise.all([
        getCountFromServer(allQuery),
        getCountFromServer(activeQuery),
        getCountFromServer(inactiveQuery),
        getCountFromServer(pendingQuery)
      ]);

      setStats({
        total: allSnap.data().count,
        active: activeSnap.data().count,
        inactive: inactiveSnap.data().count,
        pending: pendingSnap.data().count
      });
    } catch (error) {
      console.error('Statistics loading failed:', error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const accountData = {
        ...formData,
        updatedAt: new Date(),
        createdBy: 'admin'
      };

      if (modalType === 'create') {
        accountData.createdAt = new Date();
        await addDoc(collection(db, `artifacts/${appId}/remittanceAccounts`), accountData);
        showToastMessage('계좌가 성공적으로 추가되었습니다', 'success');
      } else {
        await updateDoc(doc(db, `artifacts/${appId}/remittanceAccounts`, editingAccount.id), accountData);
        showToastMessage('계좌 정보가 업데이트되었습니다', 'success');
      }

      fetchAccounts();
      loadStatistics();
      closeModal();
    } catch (error) {
      console.error('Save failed:', error);
      showToastMessage('저장에 실패했습니다', 'error');
    }
  };

  // Handle account delete
  const handleDeleteAccount = async (accountId) => {
    try {
      await deleteDoc(doc(db, `artifacts/${appId}/remittanceAccounts`, accountId));
      showToastMessage('계좌가 삭제되었습니다', 'success');
      fetchAccounts();
      loadStatistics();
    } catch (error) {
      console.error('Delete failed:', error);
      showToastMessage('삭제에 실패했습니다', 'error');
    }
  };

  // Handle modal operations
  const showModal = (type, account = null) => {
    setModalType(type);
    setEditingAccount(account);
    setIsModalOpen(true);
    
    if (account) {
      setFormData({
        name: account.name || '',
        accountNumber: account.accountNumber || '',
        bankName: account.bankName || '',
        accountHolder: account.accountHolder || '',
        status: account.status || 'active',
        description: account.description || ''
      });
    } else {
      setFormData({
        name: '',
        accountNumber: '',
        bankName: '',
        accountHolder: '',
        status: 'active',
        description: ''
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
    setFormData({
      name: '',
      accountNumber: '',
      bankName: '',
      accountHolder: '',
      status: 'active',
      description: ''
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      inactive: 'gray',
      pending: 'yellow'
    };
    return colors[status] || 'gray';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      active: CheckCircle,
      inactive: XCircle,
      pending: AlertTriangle
    };
    return icons[status] || AlertTriangle;
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'active': return '활성';
      case 'inactive': return '비활성';
      case 'pending': return '대기중';
      default: return '알수없음';
    }
  };

  // Filter data based on search criteria
  const getFilteredData = (data) => {
    return data.filter(item => {
      const matchesSearch = !searchTerm || 
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.accountNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.bankName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.accountHolder?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  };

  // Render statistics cards
  const renderStatsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">전체 계좌</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="p-3 bg-blue-100 rounded-full">
            <CreditCard className="w-6 h-6 text-blue-600" />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">활성 계좌</p>
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          </div>
          <div className="p-3 bg-green-100 rounded-full">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">비활성 계좌</p>
            <p className="text-2xl font-bold text-gray-600">{stats.inactive}</p>
          </div>
          <div className="p-3 bg-gray-100 rounded-full">
            <XCircle className="w-6 h-6 text-gray-600" />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">대기중 계좌</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="p-3 bg-yellow-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-yellow-600" />
          </div>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">송금 계좌 관리</h1>
        <p className="text-gray-600">송금 서비스에 사용되는 계좌 정보를 관리합니다</p>
      </div>

      {renderStatsCards()}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm">
        {/* Search and Filter */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="계좌명, 계좌번호, 은행명으로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={fetchAccounts}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>새로고침</span>
            </button>
            <button
              onClick={() => showModal('create')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>계좌 추가</span>
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : getFilteredData(accounts).length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <CreditCard className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-gray-500">등록된 계좌가 없습니다</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계좌 정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    은행명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계좌주
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredData(accounts).map((account) => {
                  const StatusIcon = getStatusIcon(account.status);
                  return (
                    <tr key={account.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {account.name || '이름 없음'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {account.accountNumber || '계좌번호 없음'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.bankName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.accountHolder || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getStatusColor(account.status)}-100 text-${getStatusColor(account.status)}-800`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {getStatusText(account.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => showModal('edit', account)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAccount(account.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                페이지 {currentPage} / {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Account Form Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {modalType === 'create' ? '계좌 추가' : '계좌 수정'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">계좌명</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">계좌번호</label>
                  <input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">은행명</label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">계좌주</label>
                  <input
                    type="text"
                    value={formData.accountHolder}
                    onChange={(e) => setFormData({...formData, accountHolder: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">상태</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="active">활성</option>
                    <option value="inactive">비활성</option>
                    <option value="pending">대기중</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">설명</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    {modalType === 'create' ? '추가' : '수정'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RemittanceAccountManager;